import SkeletonBox from '@src/components/common/SkeletonBox';
import styled from 'styled-components';

import type { FC } from 'react';

/** Enough cards to fill the grid on a typical viewport without overflowing it. */
const PLACEHOLDER_CARD_COUNT = 6;

/** Widths of the tab-strip placeholders, mirroring the real labels' lengths. */
const TAB_WIDTHS = ['82px', '64px', '96px', '88px', '58px', '132px', '76px'];

/**
 * The Content Studio page while its story is still loading: the same tab strip,
 * video area, description column and clip grid the page paints once the data
 * lands, so nothing moves when it does.
 *
 * Mirrors `ContentView` + `videoSection` + `SocialClipsView`; when any of those
 * layouts change, this changes with them — a skeleton that no longer matches
 * causes the layout shift it exists to prevent.
 */
const ContentStudioSkeleton: FC = () => (
  <Wrapper data-testid="content-studio-skeleton">
    <Tabs>
      {TAB_WIDTHS.map(width => (
        <SkeletonBox key={width} $width={width} $height="16px" />
      ))}
    </Tabs>

    <ContentArea>
      <StoryContent>
        <PlayerArea>
          <SkeletonBox $width="100%" $height="259px" $radius="10px" />
        </PlayerArea>
        <DescriptionArea>
          <SkeletonBox $width="120px" $height="16px" />
          <DescriptionLines>
            <SkeletonBox $height="12px" />
            <SkeletonBox $height="12px" />
            <SkeletonBox $width="72%" $height="12px" />
          </DescriptionLines>
        </DescriptionArea>
      </StoryContent>

      <ClipsHeader>
        <SkeletonBox $width="220px" $height="32px" $radius="8px" />
        <SkeletonBox $width="180px" $height="32px" $radius="8px" />
      </ClipsHeader>

      <ClipsGrid>
        {Array.from({ length: PLACEHOLDER_CARD_COUNT }, (_, i) => (
          <ClipCard key={i}>
            <ClipCardShell>
              <ClipThumbnail />
              <ClipCardInfo>
                <SkeletonBox $width="70%" $height="14px" />
                <ClipBadgeRow>
                  <SkeletonBox $width="44px" $height="20px" />
                  <SkeletonBox $width="44px" $height="20px" />
                  <SkeletonBox $width="44px" $height="20px" />
                </ClipBadgeRow>
                <SkeletonBox $width="55%" $height="11px" />
              </ClipCardInfo>
            </ClipCardShell>
          </ClipCard>
        ))}
      </ClipsGrid>
    </ContentArea>
  </Wrapper>
);

export default ContentStudioSkeleton;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

// Matches ContentView's <Tabs> strip: same gap, padding and bottom border, so
// the real tabs land exactly where their placeholders were.
const Tabs = styled.div`
  display: flex;
  align-items: center;
  gap: 0 var(--spacing-lg);
  border-bottom: 1px solid var(--border-subtle);
  padding: var(--spacing-sm) var(--spacing-sm) 0;

  > * {
    margin-bottom: var(--spacing-sm);
  }
`;

// ContentView's <ContentWrapper $topPadding="30px"> for the Video Clips tab.
const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 30px;
`;

const StoryContent = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 460px) 1fr;
  column-gap: var(--spacing-lg);
  margin-bottom: 30px;

  @media only screen and (max-width: 900px) {
    grid-template-columns: 1fr;
    row-gap: 20px;
  }
`;

const PlayerArea = styled.div`
  display: flex;
  max-width: 460px;
`;

const DescriptionArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  min-width: 0;
  align-self: start;
`;

const DescriptionLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// SocialClipsView's <Header>: the kind toggle on the left, the search and
// filter controls on the right.
const ClipsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
`;

const ClipsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  place-items: center;
  row-gap: 40px;
  column-gap: 24px;
  margin-top: 30px;
  padding-bottom: 30px;
`;

const ClipCard = styled.div`
  width: 100%;
  min-width: 280px;
  max-width: 392px;
`;

const ClipCardShell = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--surface);
  border: 1.5px solid transparent;
  border-radius: 8px;
  overflow: hidden;
`;

const ClipThumbnail = styled(SkeletonBox)`
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 */
  height: auto;
  border-radius: 0;
`;

const ClipCardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
`;

const ClipBadgeRow = styled.div`
  display: flex;
  gap: 8px;
`;
